/**
 * EncounterSlice Insurances Reducers
 * 
 * Reducers for managing encounter insurances with priority ordering.
 */

import { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { InsuranceType } from '../../types/encounterTypes';
import { EncounterSliceState } from '../state';
import { addEditedField } from '../../../shared/sliceUtils';

// =============================================================================
// INSURANCES CRUD REDUCERS
// =============================================================================

/**
 * Add a new insurance
 */
export const addInsurance: CaseReducer<EncounterSliceState, PayloadAction<InsuranceType>> = (
  state,
  action
) => {
  if (state.currentEncounter) {
    if (!state.currentEncounter.insurances) state.currentEncounter.insurances = [];
    state.currentEncounter.insurances.push(action.payload);
    addEditedField(state, 'insurances');
  }
};

/**
 * Update an existing insurance
 */
export const updateInsurance: CaseReducer<
  EncounterSliceState,
  PayloadAction<{ id: string; updates: Partial<InsuranceType> }>
> = (state, action) => {
  if (state.currentEncounter?.insurances) {
    const idx = state.currentEncounter.insurances.findIndex((i) => i.id === action.payload.id);
    if (idx !== -1) {
      state.currentEncounter.insurances[idx] = {
        ...state.currentEncounter.insurances[idx],
        ...action.payload.updates,
      };
      addEditedField(state, 'insurances');
    }
  }
};

/**
 * Remove an insurance and resequence priorities
 */
export const removeInsurance: CaseReducer<EncounterSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  if (state.currentEncounter?.insurances) {
    // Find the insurance to remove and get its priority
    const insuranceToRemove = state.currentEncounter.insurances.find((i) => i.id === action.payload);
    const removedPriority = insuranceToRemove?.priority || 0;

    // Remove the insurance
    state.currentEncounter.insurances = state.currentEncounter.insurances.filter(
      (i) => i.id !== action.payload
    );

    // Resequence priorities for insurances that had higher priorities
    state.currentEncounter.insurances = state.currentEncounter.insurances.map((insurance) => {
      if (insurance.priority && insurance.priority > removedPriority) {
        return {
          ...insurance,
          priority: insurance.priority - 1,
        };
      }
      return insurance;
    });

    addEditedField(state, 'insurances');
  }
};

/**
 * Set the entire insurances array (replace all)
 */
export const setInsurances: CaseReducer<EncounterSliceState, PayloadAction<InsuranceType[]>> = (
  state,
  action
) => {
  if (state.currentEncounter) {
    state.currentEncounter.insurances = action.payload;
    addEditedField(state, 'insurances');
  }
};

// =============================================================================
// INSURANCES PRIORITY REDUCERS
// =============================================================================

/**
 * Move an insurance up in priority
 */
export const moveInsuranceUp: CaseReducer<EncounterSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  if (state.currentEncounter?.insurances) {
    const insurances = state.currentEncounter.insurances;
    // Sort by priority first to ensure correct order
    const sortedInsurances = [...insurances].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    const insuranceIndex = sortedInsurances.findIndex((i) => i.id === action.payload);

    if (insuranceIndex > 0) {
      const currentInsurance = sortedInsurances[insuranceIndex];
      const previousInsurance = sortedInsurances[insuranceIndex - 1];

      // Swap priorities
      const tempPriority = currentInsurance.priority;
      currentInsurance.priority = previousInsurance.priority;
      previousInsurance.priority = tempPriority;

      // Update the original array with the modified insurances
      const currentOriginalIndex = insurances.findIndex((i) => i.id === currentInsurance.id);
      const previousOriginalIndex = insurances.findIndex((i) => i.id === previousInsurance.id);

      if (currentOriginalIndex !== -1) {
        insurances[currentOriginalIndex] = currentInsurance;
      }
      if (previousOriginalIndex !== -1) {
        insurances[previousOriginalIndex] = previousInsurance;
      }

      addEditedField(state, 'insurances');
    }
  }
};

/**
 * Move an insurance down in priority
 */
export const moveInsuranceDown: CaseReducer<EncounterSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  if (state.currentEncounter?.insurances) {
    const insurances = state.currentEncounter.insurances;
    // Sort by priority first to ensure correct order
    const sortedInsurances = [...insurances].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    const insuranceIndex = sortedInsurances.findIndex((i) => i.id === action.payload);

    if (insuranceIndex < sortedInsurances.length - 1 && insuranceIndex >= 0) {
      const currentInsurance = sortedInsurances[insuranceIndex];
      const nextInsurance = sortedInsurances[insuranceIndex + 1];

      // Swap priorities
      const tempPriority = currentInsurance.priority;
      currentInsurance.priority = nextInsurance.priority;
      nextInsurance.priority = tempPriority;

      // Update the original array with the modified insurances
      const currentOriginalIndex = insurances.findIndex((i) => i.id === currentInsurance.id);
      const nextOriginalIndex = insurances.findIndex((i) => i.id === nextInsurance.id);

      if (currentOriginalIndex !== -1) {
        insurances[currentOriginalIndex] = currentInsurance;
      }
      if (nextOriginalIndex !== -1) {
        insurances[nextOriginalIndex] = nextInsurance;
      }

      addEditedField(state, 'insurances');
    }
  }
};
